<script setup>
import { onMounted, onBeforeUnmount, ref, onBeforeMount, inject, computed } from 'vue'
import WOW from 'wow.js'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useAuthGlobalStore } from '@/stores/authGlobalStore'
import { XMarkIcon } from '@heroicons/vue/20/solid'
import {
  MoonIcon,
  SunIcon,
  ShoppingBagIcon,
  MapPinIcon,
  PhoneIcon,
  ClockIcon,
  TagIcon,
  MagnifyingGlassIcon,
  UserIcon,
  HomeIcon,
  TicketIcon,
  CalendarIcon,
  InboxIcon
} from '@heroicons/vue/24/outline'
import placeholderImg from '@/assets/images/placeholder_img.png'

const IS_MOBILE_APP = inject('IS_MOBILE_APP')
const restoStore = useRestoGLobalStore()
const dynamicContent = restoStore?.restoInfo?.dynamic_site_content
const address = `${restoStore?.restoInfo?.address}`
const name = `${restoStore?.restoInfo?.name}`
const query = `${name} ${address}`
const mapSrc = `https://maps.google.com/maps?q=` + encodeURI(query) + `&output=embed`
const googleAnalyticsKey = restoStore?.SITE_SETTINGS?.google_analytics_key
const heroBackgroundImage = computed(() => {
  const url = dynamicContent?.content?.rsc_slider_mobile_background_image_url
  return typeof url === 'string' && url.trim() ? url : placeholderImg
})

const date = new Date()
const currentYear = date.getFullYear()
const isSticky = ref(false)
const showMobileHeader = ref(false)
const bgColor1 = ref('')
const menuIconColourClass = ref('text-slate-900 dark:text-gray-100')
const activeCategoryId = ref(null)
const activeBottomTab = ref('explore')

// Theme handling
const isDarkMode = ref(false)
const showOfferModal = ref(false)
const authStore = useAuthGlobalStore()
const isLoggedIn = computed(() => authStore?.isLoggedIn)

const setBottomTab = (tab) => {
  activeBottomTab.value = tab
}

const isGiftVoucherCategory = (category) => {
  const categoryName = category?.category_name?.toLowerCase?.() || ''
  return categoryName === 'gift voucher' || categoryName === 'gift vouchers'
}

const menuCategories = computed(() => {
  const categories = restoStore?.MENU_DATA?.categories || []
  return categories.filter((category) => category && !isGiftVoucherCategory(category))
})

const categoryPills = computed(() => menuCategories.value.slice(0, 10))
const iconCategories = computed(() => menuCategories.value.slice(0, 6))

const popularDishes = computed(() => {
  const products = restoStore?.restoInfo?.popular_menu?.products || []
  return products
    .filter((product) => product)
    .slice(0, 8)
    .map((product) => {
      const availableItem = (product.items || []).find((item) => item?.is_available != 0)
      const fallbackItem = product.items?.[0]
      const item = availableItem || fallbackItem
      const image = product.menu_img_url || item?.menu_img_url || placeholderImg
      const variants = (product.items || [])
        .map((variant) => variant?.item_syn)
        .filter(Boolean)

      return {
        id: product.product_id,
        name: product.product_name || item?.item_name_full || item?.item_name,
        info: product.info || '',
        image,
        price: item?.price,
        variants,
        isPopular: product.is_popular == 1
      }
    })
})

const activeOffers = computed(() => {
  if (!IS_MOBILE_APP && dynamicContent?.info?.active_website_offers?.length > 0) {
    return dynamicContent.info.active_website_offers
  }
  if (IS_MOBILE_APP && dynamicContent?.info?.active_mobileapp_offers?.length > 0) {
    return dynamicContent.info.active_mobileapp_offers
  }
  return []
})

const selectCategory = (categoryId) => {
  activeCategoryId.value = categoryId
}

// Check if user has a theme preference in localStorage or prefers dark mode
const initializeTheme = () => {
  const storedTheme = localStorage.getItem('theme')
  let themePreference = null

  if (storedTheme) {
    try {
      const themeObj = JSON.parse(storedTheme)
      themePreference = themeObj.theme
    } catch (e) {
      console.error('Error parsing theme from localStorage')
      themePreference = 'dark'
    }
  }

  if (themePreference === 'light') {
    isDarkMode.value = false
    document.documentElement.classList.remove('dark')
  } else {
    isDarkMode.value = true
    document.documentElement.classList.add('dark')
    if (!storedTheme) {
      localStorage.setItem('theme', JSON.stringify({ theme: 'dark' }))
    }
  }
}

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value

  if (isDarkMode.value) {
    document.documentElement.classList.add('dark')
    localStorage.setItem('theme', JSON.stringify({ theme: 'dark' }))
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('theme', JSON.stringify({ theme: 'light' }))
  }
}

const handleScroll = () => {
  isSticky.value = window.scrollY > 50
}
const handleShowHeader = () => {
  showMobileHeader.value = !showMobileHeader.value
}

const handleNavLinkClick = () => {
  showMobileHeader.value = false
}

const closeOfferModal = () => {
  showOfferModal.value = false
}

const initializeOfferModal = () => {
  if (
    dynamicContent?.content?.rsc_dynamicsite_popup_enabled &&
    dynamicContent?.content?.rsc_dynamicsite_popup_image_url
  ) {
    setTimeout(() => {
      showOfferModal.value = true
    }, 2000)
  }
}

const loadThemeStyle = async (styleLoader) => {
  try {
    await styleLoader()
  } catch (error) {
    window.dispatchEvent(new Event('vite:preloadError'))
  }
}

onBeforeMount(() => {
  initializeTheme()

  if (dynamicContent?.mobile_style == 1) {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template1/css/style1.css'))
    bgColor1.value = '#991b1b'
  } else if (dynamicContent?.mobile_style == 2) {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template1/css/style2.css'))
    bgColor1.value = '#fbaf32'
  } else if (dynamicContent?.mobile_style == 3) {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template1/css/style3.css'))
    bgColor1.value = '#49a003'
  } else if (dynamicContent?.mobile_style == 4) {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template1/css/style4.css'))
    bgColor1.value = '#cda45e'
  } else {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template1/css/style1.css'))
    bgColor1.value = '#991b1b'
  }
})
onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  new WOW().init()
  const favicon = document.querySelector('link[rel="icon"]')
  if (favicon) {
    favicon.href = `${dynamicContent?.info?.logo}`
  } else {
    const newFavicon = document.createElement('link')
    newFavicon.rel = 'icon'
    newFavicon.type = 'image/x-icon'
    newFavicon.href = `${dynamicContent?.info?.logo}`
    document.head.appendChild(newFavicon)
  }

  if (googleAnalyticsKey) {
    const script = document.createElement('script')
    script.src = `https://www.googletagmanager.com/gtm.js?id=${googleAnalyticsKey}`
    document.head.appendChild(script)
    const noscript = document.createElement('noscript')
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${googleAnalyticsKey}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
    document.head.appendChild(noscript)
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.addEventListener('change', initializeTheme)

  initializeOfferModal()
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.removeEventListener('change', initializeTheme)
})
</script>
<template>
  <div
    class="temp-w1 food-app transition-colors duration-300 bg-[#f6f7f9] dark:bg-gray-950"
    ref="mobileMenuRef"
  >
    <!-- Header -->
    <header
      :class="{ 'sticky-header': isSticky }"
      class="bg-white/90 dark:bg-gray-900/90 sticky top-0 z-50 relative pt-safe transition-colors duration-300 border-b border-slate-100 dark:border-gray-800 backdrop-blur-md"
    >
      <div class="container mx-auto flex items-center justify-between py-3 px-4 sm:px-6 lg:px-8">
        <div class="flex items-center min-w-0 gap-3">
          <img
            :src="dynamicContent?.info?.logo"
            alt="Logo"
            class="h-11 w-11 rounded-2xl object-cover shadow-sm ring-1 ring-black/5"
          />
          <div class="min-w-0 hidden sm:block">
            <p class="text-xs uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
              Welcome
            </p>
            <p class="truncate text-sm font-semibold text-slate-900 dark:text-white">
              {{ dynamicContent?.info?.name }}
            </p>
          </div>
        </div>

        <nav class="hidden md:flex items-center gap-3 lg:gap-7">
          <a href="#home" class="nav-link">Home</a>
          <a href="#categories" class="nav-link">Menu</a>
          <a href="#hours" class="nav-link">Hours</a>
          <a href="#find-us" class="nav-link">Find Us</a>
          <router-link
            v-if="dynamicContent?.info?.is_reservation_active"
            class="btn px-5 py-2 rounded-xl text-white text-sm font-semibold shadow-sm"
            :to="{ name: 'reservation', params: { resto_id: restoStore?.RESTO_ID } }"
            >Book Table</router-link
          >
        </nav>

        <div class="flex items-center gap-2">
          <button
            @click="toggleDarkMode"
            class="p-2.5 rounded-xl bg-slate-100 dark:bg-gray-800 transition-colors duration-300 hover:bg-slate-200 dark:hover:bg-gray-700"
            aria-label="Toggle dark mode"
          >
            <SunIcon v-if="isDarkMode" class="h-5 w-5 text-amber-400" />
            <MoonIcon v-else class="h-5 w-5 text-slate-700" />
          </button>

          <router-link
            v-if="restoStore?.MENU_URL"
            class="inline-flex btn items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-white text-xs sm:text-sm font-semibold shadow-sm"
            :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
          >
            <ShoppingBagIcon class="h-4 w-4" />
            <span class="whitespace-nowrap">Order Online</span>
          </router-link>

          <button class="md:hidden focus:outline-none p-1" @click="handleShowHeader()">
            <svg
              class="h-6 w-6"
              :class="menuIconColourClass"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              v-if="!showMobileHeader"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16m-7 6h7"
              ></path>
            </svg>
            <XMarkIcon
              v-if="showMobileHeader"
              class="h-6 w-6 text-black dark:text-white transition-colors duration-200"
              aria-hidden="true"
            />
          </button>
        </div>

        <div
          :class="[showMobileHeader ? 'block' : 'hidden']"
          class="mobile-menu bg-white dark:bg-gray-900 absolute left-0 right-0 top-[70px] pb-4 mt-safe transition-colors duration-300 shadow-lg border-t border-slate-100 dark:border-gray-800"
        >
          <a href="#home" class="mobile-link" @click="handleNavLinkClick">Home</a>
          <a href="#categories" class="mobile-link" @click="handleNavLinkClick">Menu</a>
          <a href="#hours" class="mobile-link" @click="handleNavLinkClick">Hours</a>
          <router-link
            v-if="restoStore?.MENU_URL"
            class="mobile-link"
            :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
            >Online Order</router-link
          >
          <a href="#find-us" class="mobile-link" @click="handleNavLinkClick">Find Us</a>
          <router-link
            v-if="dynamicContent?.info?.is_reservation_active"
            class="btn rounded-xl text-white text-sm font-semibold py-2.5 px-4 mx-4 mt-3 inline-block"
            :to="{ name: 'reservation', params: { resto_id: restoStore?.RESTO_ID } }"
            >Book Table</router-link
          >
        </div>
      </div>
    </header>

    <!-- Offer text -->
    <div
      v-if="dynamicContent?.content?.rsc_resto_note_customer_message"
      class="container-fluid pt-1 px-3 bg-white dark:bg-gray-900 border-b border-slate-100 dark:border-gray-800 transition-colors duration-300"
    >
      <div class="top-notice-bar">
        <div class="flex">
          <div
            class="flex-none w-1/6 md:w-1/12 lg:w-1/12 xl:w-1/12 text-slate-800 dark:text-gray-200 font-semibold"
          >
            Notice!
          </div>
          <div class="flex-grow w-5/6 md:w-11/12 lg:w-11/12 xl:w-11/12 text-slate-700 dark:text-gray-300">
            <marquee
              class="hover:cursor-pointer"
              onmouseover="this.stop();"
              onmouseout="this.start();"
            >
              {{ dynamicContent?.content?.rsc_resto_note_customer_message }}
            </marquee>
          </div>
        </div>
      </div>
    </div>

    <main class="px-safe dark:text-gray-200 transition-colors duration-300 pb-4 md:pb-0">
      <!-- Hero -->
      <section id="home" class="relative overflow-hidden">
        <div
          class="hero-panel relative min-h-[72vh] md:min-h-[78vh] flex items-center"
          :style="{
            background: `linear-gradient(180deg, rgba(15,23,42,0.15) 0%, rgba(15,23,42,0.72) 55%, rgba(15,23,42,0.92) 100%), url(${heroBackgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }"
        >
          <div class="hero-glow" aria-hidden="true"></div>
          <div class="container mx-auto px-4 sm:px-6 lg:px-8 pb-12 pt-24 md:py-20 relative z-10">
            <div class="max-w-2xl text-white wow fadeInUp animated">
              <p class="hero-kicker mb-3">Order from your favourite kitchen</p>
              <h1 class="hero-title mb-4">
                {{ dynamicContent?.info?.name }}
              </h1>
              <p class="text-base md:text-lg text-white/80 max-w-xl mb-5">
                Fresh flavours, fast ordering, and delivery made simple — browse categories and
                place your order online in seconds.
              </p>

              <div class="flex flex-wrap items-center gap-3">
                <router-link
                  v-if="restoStore?.MENU_URL"
                  class="btn inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl text-white text-base md:text-lg font-semibold shadow-lg hero-cta"
                  :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
                >
                  <ShoppingBagIcon class="h-5 w-5" />
                  Order Online
                </router-link>
                <router-link
                  v-if="dynamicContent?.info?.is_reservation_active"
                  class="inline-flex items-center px-6 py-3.5 rounded-2xl text-white text-base font-semibold border border-white/40 bg-white/10 backdrop-blur-sm hover:bg-white/20 transition"
                  :to="{ name: 'reservation', params: { resto_id: restoStore?.RESTO_ID } }"
                  >Book Table</router-link
                >
                <a
                  href="#featured"
                  class="inline-flex items-center px-5 py-3.5 rounded-2xl text-white/90 text-sm font-medium hover:text-white transition"
                >
                  See popular dishes →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Offers (ticket / voucher cards) -->
      <section
        v-if="activeOffers.length"
        id="offers"
        class="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 relative z-20 pt-safe"
      >
        <div class="flex items-end justify-between gap-3 mb-4">
          <div>
            <p class="section-eyebrow">Limited time</p>
            <h2 class="section-heading">Today’s offers</h2>
          </div>
          <router-link
            v-if="restoStore?.MENU_URL"
            class="btn hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold"
            :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
          >
            Claim deal
          </router-link>
        </div>
        <div class="flex gap-4 overflow-x-auto hide-scrollbar pb-1 -mx-1 px-1">
          <article
            v-for="(offer, index) in activeOffers"
            :key="index"
            class="offer-ticket"
          >
            <div class="offer-ticket-left">
              <TagIcon class="h-6 w-6 text-white" />
              <span class="offer-ticket-code">DEAL {{ index + 1 }}</span>
            </div>
            <div class="offer-ticket-perforation" aria-hidden="true"></div>
            <div class="offer-ticket-body">
              <p class="offer-ticket-text">{{ offer }}</p>
              <router-link
                v-if="restoStore?.MENU_URL"
                class="offer-ticket-cta"
                :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
              >
                Order now
                <ArrowRightIcon class="h-4 w-4" />
              </router-link>
            </div>
          </article>
        </div>
      </section>

      <!-- Categories (pills + circular icons, like Template2) -->
      <section
        v-if="menuCategories.length"
        id="categories"
        class="container mx-auto px-4 sm:px-6 lg:px-8 pt-safe"
        :class="activeOffers.length ? 'pt-2' : '-mt-8 relative z-20'"
      >
        <div class="flex items-end justify-between">
            <div>
              <p class="section-eyebrow">Browse</p>
              <h2 class="section-heading">Categories</h2>
            </div>
            <router-link
              v-if="restoStore?.MENU_URL"
              class="text-sm font-bold accent-text"
              :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
              >See all</router-link
            >
          </div>
        <div
          v-if="categoryPills.length"
          class="flex gap-2 overflow-x-auto py-4 hide-scrollbar"
        >
          <component
            :is="restoStore?.MENU_URL ? 'router-link' : 'button'"
            v-for="category in categoryPills"
            :key="`pill-${category.category_id}`"
            v-bind="
              restoStore?.MENU_URL
                ? { to: { name: 'menu', params: { menu_slug: restoStore.MENU_URL } } }
                : { type: 'button' }
            "
            class="pill"
            :class="{ 'is-active': activeCategoryId === category.category_id }"
            @click="selectCategory(category.category_id)"
          >
            {{ category.category_name }}
          </component>
        </div>
      </section>

      <!-- Popular dishes -->
      <section
        v-if="popularDishes.length"
        id="featured"
        class="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16"
      >
        <div class="flex items-end justify-between gap-3 mb-6">
          <div>
            <p class="section-eyebrow">Customer favourites</p>
            <h2 class="section-heading">Popular dishes</h2>
          </div>
          <router-link
            v-if="restoStore?.MENU_URL"
            class="btn hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold"
            :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
          >
            Order Online
          </router-link>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <article
            v-for="(dish, index) in popularDishes"
            :key="dish.id || index"
            class="dish-card group"
          >
            <div class="dish-media">
              <img :src="dish.image" :alt="dish.name" class="dish-image" loading="lazy" />
              <span v-if="dish.isPopular" class="dish-badge">Popular</span>
            </div>
            <div class="dish-body">
              <h3 class="dish-title">
                {{ dish.name }}
                <span
                  v-if="dish.variants?.length > 1"
                  class="text-sm font-semibold text-slate-500 dark:text-gray-400"
                >
                  ({{ dish.variants.slice(0, 3).join(', ')
                  }}{{ dish.variants.length > 3 ? '...' : '' }})
                </span>
              </h3>
              <p v-if="dish.info" class="dish-info">{{ dish.info }}</p>
              <div class="flex items-center justify-between gap-3 mt-4">
                <p v-if="dish.price != null" class="dish-price">£{{ dish.price }}</p>
                <span v-else class="dish-price text-sm opacity-70">From menu</span>
                <router-link
                  v-if="restoStore?.MENU_URL"
                  class="btn dish-order-btn"
                  :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
                  >Order</router-link
                >
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- Opening hours -->
      <section
        v-if="dynamicContent?.info?.resto_opening_times?.consolidated?.length"
        id="hours"
        class="container mx-auto px-4 sm:px-6 lg:px-8 pb-12 md:pb-16"
      >
        <div class="hours-panel">
          <div class="hours-panel-header">
            <div class="hours-icon-wrap">
              <ClockIcon class="h-7 w-7 text-white" />
            </div>
            <div>
              <p class="section-eyebrow mb-1">When to visit</p>
              <h2 class="section-heading">Opening hours</h2>
            </div>
          </div>
          <div class="hours-grid">
            <div
              v-for="openTime in dynamicContent?.info?.resto_opening_times?.consolidated"
              :key="openTime?.day_numbers"
              class="hours-row"
            >
              <p class="hours-days">{{ openTime.days }}</p>
              <p class="hours-times">
                <span v-for="(time, index) in openTime.am_pm_times" :key="index">
                  {{ time
                  }}<span v-if="index < openTime.am_pm_times.length - 1">,&nbsp;</span>
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Find us / Map -->
      <section
        id="find-us"
        class="container mx-auto px-4 sm:px-6 lg:px-8 pb-12 md:pb-16"
      >
        <div class="map-panel">
          <div class="map-panel-copy">
            <p class="section-eyebrow">Location</p>
            <h2 class="section-heading mb-4">Find us</h2>
            <p class="text-slate-600 dark:text-gray-300 mb-6 leading-relaxed">
              Drop by or call ahead — we’re ready when you are.
            </p>
            <div class="space-y-4 mb-6">
              <div
                v-if="dynamicContent?.info?.address"
                class="flex items-start gap-3 text-slate-700 dark:text-gray-200"
              >
                <span class="map-info-icon">
                  <MapPinIcon class="h-5 w-5" />
                </span>
                <span>{{ dynamicContent?.info?.address }}</span>
              </div>
              <a
                v-if="dynamicContent?.info?.phone_number"
                :href="`tel:${dynamicContent?.info?.phone_number}`"
                class="flex items-center gap-3 text-slate-700 dark:text-gray-200 hover:opacity-80 transition"
              >
                <span class="map-info-icon">
                  <PhoneIcon class="h-5 w-5" />
                </span>
                <span>{{ dynamicContent?.info?.phone_number }}</span>
              </a>
            </div>
            <div class="flex flex-wrap gap-3">
              <a
                :href="`https://maps.google.com/maps?q=${encodeURIComponent(query)}`"
                target="_blank"
                rel="noopener noreferrer"
                class="btn inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold"
              >
                <MapPinIcon class="h-4 w-4" />
                Open in Maps
              </a>
              <router-link
                v-if="restoStore?.MENU_URL"
                class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border border-slate-200 dark:border-gray-700 text-slate-800 dark:text-gray-200 hover:bg-slate-50 dark:hover:bg-gray-800 transition"
                :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
              >
                <ShoppingBagIcon class="h-4 w-4" />
                Order Online
              </router-link>
            </div>
          </div>
          <div class="map-frame wow zoomIn general_grow animated">
            <iframe
              width="100%"
              height="100%"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              :src="mapSrc"
              title="Restaurant location map"
              class="absolute inset-0 h-full w-full border-0"
            >
            </iframe>
          </div>
        </div>
      </section>

    </main>

    <!-- Footer -->
    <footer
      class="bg-white dark:bg-gray-900 transition-colors duration-300 pt-10 pb-6 px-3 pb-safe border-t border-slate-100 dark:border-gray-800"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="flex items-center justify-center gap-5 mb-5">
          <a
            v-if="dynamicContent?.content?.rsc_social_media_link_facebook"
            :href="dynamicContent?.content?.rsc_social_media_link_facebook"
            target="_blank"
            class="social-icon"
            aria-label="Facebook"
          >
            <i class="fa fa-facebook" aria-hidden="true"></i>
          </a>
          <a
            v-if="dynamicContent?.content?.rsc_social_media_link_instagram"
            :href="dynamicContent?.content?.rsc_social_media_link_instagram"
            target="_blank"
            class="social-icon"
            aria-label="Instagram"
          >
            <i class="fa fa-instagram" aria-hidden="true"></i>
          </a>
          <a
            v-if="dynamicContent?.content?.rsc_social_media_link_twitter"
            :href="dynamicContent?.content?.rsc_social_media_link_twitter"
            target="_blank"
            class="social-icon"
            aria-label="Twitter"
          >
            <i class="fa fa-twitter" aria-hidden="true"></i>
          </a>
        </div>
        <p class="mb-0 text-slate-500 dark:text-gray-400 text-sm">
          &copy; {{ currentYear }} - All rights reserved
        </p>
      </div>
    </footer>

    <!-- Mobile bottom navigation -->
    <nav
      class="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-gray-900 border-t border-slate-200 dark:border-gray-800 pb-safe shadow-[0_-4px_20px_rgba(0,0,0,0.06)]"
      aria-label="Primary"
    >
      <div class="grid grid-cols-4 h-[4.25rem]">
        <a
          href="#home"
          class="bottom-tab"
          :class="{ 'is-active': activeBottomTab === 'explore' }"
          @click="setBottomTab('explore')"
        >
          <HomeIcon class="h-6 w-6" />
          <span>Home</span>
        </a>
        <router-link
          v-if="restoStore?.MENU_URL"
          :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
          class="bottom-tab"
          :class="{ 'is-active': activeBottomTab === 'search' }"
          @click="setBottomTab('search')" 
        >
          <MagnifyingGlassIcon class="h-6 w-6" />
          <span>Menu</span>
        </router-link>
        <a
          v-else
          href="#categories"
          class="bottom-tab"
          :class="{ 'is-active': activeBottomTab === 'search' }"
          @click="setBottomTab('search')"
        >
          <TicketIcon class="h-6 w-6" />
          <span>Offers</span>
        </a>
        <a
          :href="activeOffers.length ? '#offers' : '#categories'"
          class="bottom-tab"
          :class="{ 'is-active': activeBottomTab === 'deals' }"
          @click="setBottomTab('deals')"
        >
          <CalendarIcon class="h-6 w-6" />
          <span>Bookings</span>
        </a>
        <router-link
          v-if="isLoggedIn"
          :to="{ name: 'my_orders' }"
          class="bottom-tab"
          :class="{ 'is-active': activeBottomTab === 'inbox' }"
          @click="setBottomTab('inbox')"
        >
          <InboxIcon class="h-6 w-6" />
          <span>My Orders</span>
        </router-link>
        <router-link
          v-else
          :to="{ name: 'login' }"
          class="bottom-tab"
          :class="{ 'is-active': activeBottomTab === 'account' }"
          @click="setBottomTab('account')"
        >
          <UserIcon class="h-6 w-6" />
          <span>Login</span>
        </router-link>
      </div>
    </nav>

    <a
      id="back-to-top"
      href="#"
      class="fixed bottom-24 md:bottom-4 right-4 rounded-full p-2.5 bg-white dark:bg-gray-800 shadow-md hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors duration-300 z-30 border border-slate-100 dark:border-gray-700"
    >
      <i
        class="fa fa-chevron-up text-slate-700 dark:text-gray-200 transition-colors duration-300"
        aria-hidden="true"
      ></i>
    </a>

    <!-- Offer Modal -->
    <div
      v-if="showOfferModal"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      @click.self="closeOfferModal"
    >
      <div
        class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl md:max-w-2xl max-w-lg w-full max-h-[90vh] overflow-hidden animate-fade-in-up transition-colors duration-300"
      >
        <button
          @click="closeOfferModal"
          class="absolute top-3 right-3 z-10 p-1 rounded-full bg-gray-900 bg-opacity-70 text-white hover:bg-opacity-90 transition-all duration-200"
          aria-label="Close offer"
        >
          <XMarkIcon class="md:h-6 md:w-6 h-5 w-5" />
        </button>

        <div class="relative">
          <img
            :src="dynamicContent?.content?.rsc_dynamicsite_popup_image_url"
            :alt="dynamicContent?.info?.name + ' Special Offer'"
            class="w-full h-auto object-cover rounded-2xl"
            @error="closeOfferModal"
          />

          <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
            <h3 class="text-white md:text-lg text-base font-bold">
              {{ dynamicContent?.info?.name }}
            </h3>
            <p
              v-if="dynamicContent?.content?.rsc_dynamicsite_popup_text_msg"
              class="text-white md:text-sm text-xs opacity-90"
            >
              {{ dynamicContent?.content?.rsc_mobileapp_popup_text_msg }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '@/layouts/dynamicSite/template1/css/responsive.css';
@import 'https://maxcdn.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css';
@import '@/layouts/dynamicSite/common/css/animate.css';
@import 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=Fraunces:opsz,wght@9..144,600;9..144,700&display=swap';

:root {
  color-scheme: light dark;
}

.food-app {
  font-family: 'Outfit', system-ui, sans-serif;
}

.nav-link {
  @apply text-sm font-semibold tracking-wide uppercase text-slate-700 hover:text-slate-950 transition-colors duration-200 dark:text-gray-300 dark:hover:text-white;
}

.mobile-link {
  @apply block uppercase py-3 px-5 text-sm font-semibold text-slate-800 hover:bg-slate-50 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white transition-colors duration-200;
}

.hero-kicker {
  font-family: 'Outfit', sans-serif;
  @apply text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] text-white/75;
}

.hero-title {
  font-family: 'Fraunces', Georgia, serif;
  @apply text-4xl sm:text-5xl md:text-7xl font-bold leading-[1.05] tracking-tight;
}

.hero-glow {
  position: absolute;
  inset: auto -10% -20% auto;
  width: min(55vw, 420px);
  height: min(55vw, 420px);
  border-radius: 9999px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.18), transparent 70%);
  pointer-events: none;
  animation: float-glow 8s ease-in-out infinite;
}

.hero-cta {
  animation: soft-pulse 2.8s ease-in-out infinite;
}

.hero-contact {
  @apply flex flex-col gap-2.5 max-w-xl;
}

.hero-contact-row {
  @apply inline-flex items-start gap-2.5 text-sm sm:text-base text-white/90;
}

.section-eyebrow {
  @apply flex text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 mb-2;
}

.section-heading {
  font-family: 'Fraunces', Georgia, serif;
  @apply text-3xl md:text-4xl font-bold text-slate-900 dark:text-white;
}

.accent-text {
  color: v-bind(bgColor1);
}

.hide-scrollbar {
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

/* Ticket-style offers (distinct from Template2 promo banner) */
.offer-ticket {
  @apply relative flex min-w-[17.5rem] max-w-[20rem] flex-shrink-0 overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-lg border border-slate-100 dark:border-gray-800;
}

.offer-ticket-left {
  @apply flex w-[4.75rem] flex-col items-center justify-center gap-2 px-2 py-4 text-center text-white;
  background: v-bind(bgColor1);
}

.offer-ticket-code {
  @apply text-[10px] font-bold uppercase tracking-[0.12em] leading-tight;
}

.offer-ticket-perforation {
  position: relative;
  width: 14px;
  flex-shrink: 0;
  background-image: radial-gradient(circle, transparent 5px, #fff 5.5px);
  background-size: 14px 18px;
  background-position: -7px 0;
  background-repeat: repeat-y;
}
:global(.dark) .offer-ticket-perforation {
  background-image: radial-gradient(circle, transparent 5px, #111827 5.5px);
}
.offer-ticket-perforation::before,
.offer-ticket-perforation::after {
  content: '';
  position: absolute;
  left: 50%;
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background: #f6f7f9;
  transform: translateX(-50%);
}
:global(.dark) .offer-ticket-perforation::before,
:global(.dark) .offer-ticket-perforation::after {
  background: #030712;
}
.offer-ticket-perforation::before {
  top: -6px;
}
.offer-ticket-perforation::after {
  bottom: -6px;
}

.offer-ticket-body {
  @apply flex flex-1 flex-col justify-between gap-3 p-4;
}

.offer-ticket-text {
  @apply text-sm font-semibold leading-snug text-slate-800 dark:text-gray-100;
}

.offer-ticket-cta {
  @apply inline-flex items-center gap-1 text-sm font-bold no-underline;
  color: v-bind(bgColor1);
}

/* Template2-style category pills + circular icons */
.pill {
  @apply inline-flex items-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-slate-700 dark:text-gray-200 transition no-underline shadow-sm;
}
.pill.is-active,
.pill:hover {
  color: #fff;
  border-color: transparent;
  background: v-bind(bgColor1);
}

.icon-cat {
  @apply flex flex-col items-center gap-2 min-w-[4.5rem] no-underline;
}

.icon-cat-avatar {
  @apply h-16 w-16 rounded-full flex items-center justify-center text-lg font-extrabold text-white shadow-md;
  background: color-mix(in srgb, v-bind(bgColor1) 88%, white);
}

.icon-cat-label {
  @apply text-[11px] font-semibold text-center text-slate-700 dark:text-gray-300 max-w-[4.5rem] line-clamp-2;
}

.dish-card {
  @apply rounded-3xl overflow-hidden bg-white dark:bg-gray-900 border border-slate-100 dark:border-gray-800 shadow-md shadow-slate-200/40 dark:shadow-black/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl;
}

.dish-media {
  @apply relative h-44 overflow-hidden;
}

.dish-image {
  @apply h-full w-full object-cover transition-transform duration-500 group-hover:scale-105;
}

.dish-badge {
  @apply absolute top-3 left-3 rounded-lg bg-black/50 dark:bg-white/50 text-white dark:text-black text-[11px] font-semibold px-2.5 py-1 backdrop-blur-sm;
}

.dish-body {
  @apply p-4;
}

.dish-title {
  @apply text-lg font-bold text-slate-900 dark:text-white line-clamp-1;
}

.dish-info {
  @apply mt-1 text-sm text-slate-500 dark:text-gray-400 line-clamp-2 min-h-[2.5rem];
}

.dish-price {
  @apply text-lg font-bold;
  color: v-bind(bgColor1);
}

.dish-order-btn {
  @apply px-3.5 py-1.5 rounded-xl text-white text-sm font-semibold;
}

.hours-panel {
  @apply rounded-3xl overflow-hidden border border-slate-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl shadow-slate-200/50 dark:shadow-black/25 p-6 sm:p-8;
  background-image: radial-gradient(
    circle at top right,
    color-mix(in srgb, v-bind(bgColor1) 14%, transparent),
    transparent 45%
  );
}

.hours-panel-header {
  @apply flex items-center gap-4 mb-6 pb-5 border-b border-slate-100 dark:border-gray-800;
}

.hours-icon-wrap {
  @apply h-14 w-14 rounded-2xl flex items-center justify-center shrink-0 shadow-md;
  background: v-bind(bgColor1);
}

.hours-grid {
  @apply grid sm:grid-cols-2 gap-3;
}

.hours-row {
  @apply flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 rounded-2xl bg-slate-50 dark:bg-gray-800/70 px-4 py-3.5 border border-slate-100/80 dark:border-gray-700/60;
}

.hours-days {
  @apply text-sm font-bold text-slate-900 dark:text-white;
}

.hours-times {
  @apply text-sm text-slate-600 dark:text-gray-300;
}

.map-panel {
  @apply grid lg:grid-cols-2 gap-0 overflow-hidden rounded-3xl border border-slate-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl shadow-slate-200/50 dark:shadow-black/25;
}

.map-panel-copy {
  @apply p-6 sm:p-8 lg:p-10 flex flex-col justify-center order-2 lg:order-1;
}

.map-info-icon {
  @apply h-10 w-10 rounded-xl flex items-center justify-center shrink-0 text-white;
  background: v-bind(bgColor1);
}

.map-frame {
  @apply relative min-h-[280px] h-72 sm:h-96 lg:h-full lg:min-h-[420px] order-1 lg:order-2;
}

.social-icon {
  @apply h-11 w-11 rounded-xl inline-flex items-center justify-center text-lg text-white transition-transform duration-200 hover:-translate-y-0.5;
  background: v-bind(bgColor1);
}

.bottom-tab {
  @apply flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 no-underline transition-colors;
}
.bottom-tab.is-active {
  color: v-bind(bgColor1);
  font-weight: 700;
}

.sticky-header {
  @apply shadow-lg bg-white/95 dark:bg-gray-900/95 backdrop-blur-md;
  transition: all 0.3s ease;
}

.top-notice-bar {
  font-weight: 500;
}

@keyframes subtle-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.top-notice-bar .flex-none {
  animation: subtle-pulse 2s infinite ease-in-out;
}

@keyframes float-glow {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  50% {
    transform: translateY(-12px);
    opacity: 1;
  }
}

@keyframes soft-pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.18);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.22);
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-fade-in-up {
  animation: fade-in-up 0.3s ease-out;
}

.z-\[9999\] {
  z-index: 9999;
}

footer {
  transition: all 0.3s ease;
  border-top-width: 1px;
  position: relative;
}

@media (max-width: 640px) {
  .hero-panel {
    min-height: 68vh;
  }
}
</style>
